"use client";

import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { Sparkles } from "lucide-react";

interface EmptyStateProps {
  title?: string;
  message?: string;
  icon?: React.ReactNode;
}

export function EmptyState({ 
  title = "Exciting Things Are Brewing", 
  message = "We're currently curating this section. Check back soon for fresh, valuable content designed just for you.",
  icon
}: EmptyStateProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const iconContainerRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const dotsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!containerRef.current) return;

    const ctx = gsap.context(() => {
      // Container enter
      gsap.fromTo(containerRef.current, 
        { opacity: 0, scale: 0.95, y: 10 }, 
        { opacity: 1, scale: 1, y: 0, duration: 0.8, ease: "power2.out" }
      );

      // Icon container float
      gsap.to(iconContainerRef.current, {
        y: -10,
        duration: 2,
        yoyo: true,
        repeat: -1,
        ease: "sine.inOut"
      });

      // Ring rotation
      gsap.to(ringRef.current, {
        rotation: 360,
        duration: 30,
        repeat: -1,
        ease: "linear"
      });

      // Dots pulse
      dotsRef.current.forEach((dot, i) => {
        if (!dot) return;
        gsap.to(dot, {
          opacity: 1,
          scale: 1,
          duration: 1,
          yoyo: true,
          repeat: -1,
          delay: i * 0.4,
          ease: "sine.inOut"
        });
      });
    });

    return () => ctx.revert();
  }, []);

  return (
    <div 
      ref={containerRef}
      className="w-full py-24 flex flex-col items-center justify-center text-center px-6 relative overflow-hidden opacity-0"
    >
      {/* Background calming ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-teal-50/50 rounded-full blur-3xl -z-10 pointer-events-none" />
      
      {/* Animated Icon Container */}
      <div 
        ref={iconContainerRef}
        className="w-20 h-20 rounded-full bg-gradient-to-tr from-teal-50 to-white border border-teal-100 flex items-center justify-center mb-8 shadow-[0_10px_40px_rgba(13,148,136,0.1)] relative"
      >
        <div
          ref={ringRef}
          className="absolute inset-0 rounded-full border border-dashed border-teal-200"
        />
        {icon || <Sparkles className="w-8 h-8 text-teal-600 opacity-80" strokeWidth={1.5} />}
      </div>

      {/* Typography */}
      <h3 className="font-serif text-[24px] md:text-[28px] font-bold text-gray-800 mb-3 tracking-tight relative z-10">
        {title}
      </h3>
      
      <p className="font-sans text-[15px] md:text-[16px] text-gray-500 max-w-[450px] leading-relaxed relative z-10">
        {message}
      </p>

      {/* Subtle indicator dots */}
      <div className="flex gap-2 mt-10">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            ref={(el) => {
              dotsRef.current[i] = el;
            }}
            className="w-2 h-2 rounded-full bg-teal-400/40 opacity-30 scale-75"
          />
        ))}
      </div>
    </div>
  );
}
